<template>
  <div class="card card-scenario mb-4">
    <div class="card-body text-center">

        <div>
          <i class="bi fs-1" :class="exclaimationOrCheck"></i>
          <h3 :class="textClass">Scenario {{ number }}</h3>
          <p>{{ scenarioName }}</p>
        </div>
        <div>
          <i
            v-if="showGraphButton"
            class="bi fs-4 bi-bar-chart-fill text-secondary float-end me-1"
            @click="onShowGraphsClicked"
          ></i>
        </div>

      <div v-if="showSolvePass" class="mt-2 mb-3">
        <div>
          <p class="mx-5 mb-3">{{ text('tests.success.description') }}</p>
        </div>
      </div>
      <div v-if="showSolveFail" class=" mt-2 mb-3">
        <div>
          <p id="solveText" class="mx-5 mb-3">
            {{ failedScenarioText }}
          </p>
          <solve-buttons
            ref="solveButtons"
            @solve-take-less="$emit('solve-take-less', scenario)"
            @solve-pay-more="$emit('solve-pay-more', scenario)"
            @solve-equity-release="$emit('solve-equity-release', scenario)"
            :scenario="scenario"
          />
        </div>
      </div>
      <div v-if="showGuaranteedIncomeOffer" class="mt-2 mb-3">
        <div>
          <template v-if="guaranteedIncomeOfferIsAdviceOnly">
              <p class="mx-5 mb-3 guaranteedIncomeText">
                  {{ text('guaranteed-income.advice-only.description') }}
              </p>
              <p class="mx-5 mb-3 guaranteedIncomeText">
                  You may wish to consider buying some guaranteed income (an annuity) to reduce this risk. If you want to consider this further you can go
                  to <a href="https://www.moneyhelper.org.uk/en/pensions-and-retirement/taking-your-pension/compare-annuities" target="_blank">Money Helper</a> for
                  more information.
              </p>
          </template>
          <template v-else>
            <p class="mx-5 mb-3 guaranteedIncomeText">
              Your plan works, but there is always a risk the future won't be
              exactly as expected. You can see what may happen if you buy some
              guaranteed income. This will reduce the risk of running out of
              money if things don't go to plan.
            </p>
            <button
              class="btn btn-success mx-3"
              @click="onExamineGuaranteedIncome"
              :disabled="disableButtons"
            >
              Let's see
            </button>
          </template>
        </div>
      </div>
      <div v-if="showGuaranteedIncomeUptake" class="mt-2 mb-3">
        <div>
          <p id="guaranteedIncomeUptakeText1">
            {{ guaranteedIncomeUptakeText }}
            <help
              title="Guaranteed income"
              :text="HelpText.guaranteedIncome(annuityType)"
            />
          </p>
          <button
            type="button"
            class="btn btn-primary btn-sm mb-3"
            @click="onUnsubscribeGuaranteedIncomeClicked($event)"
          >
            <span class="d-none d-sm-inline-block me-2">Remove</span>
            <i class="bi bi-dash-circle"></i>
          </button>
          <p id="guaranteedIncomeUptakeText2">
            <b>Don't stop here.</b> See what happens if things don't go as
            expected in the next page, then see all your plan details on our
            Finish page.
          </p>
        </div>
      </div>
      <div v-if="showFinishEncouragement" class="mt-2 mb-3">
        <div>
          <p id="finishEncouragementText">
            If everything turns out as expected your pots should not run out.
            <b>Don't stop here.</b> See what happens if things don't go as
            expected in the next page, then see all your plan details in our
            Finish page.
          </p>
        </div>
      </div>
    </div>
  </div>

  <modal
    ref="guaranteedIncomeModal"
    title="Guaranteed income"
    :idKey="'guaranteedIncomeModal' + scenario"
    :key="'guaranteedIncomeModal' + scenario"
  >
    <guaranteed-income-checker
      ref="guaranteedIncomeChecker"
      @check-finished="onGuaranteedIncomeCheckFinished"
      :key="'guaranteedIncomeChecker' + scenario"
    />
  </modal>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { Scenario } from "@/guiide/ts/scenario";
import ScenarioType from "@/guiide/ts/scenarioType";
import ScenarioOutcome from "@/guiide/ts/scenarioOutcome";
import ScenarioInfo from "@/guiide/ts/scenarioInfo";
import SolveButtons from "@/guiide/components/scenarios/SolveButtons.vue";
import Modal from "@/guiide/components/common/Modal.vue";
import GuaranteedIncomeChecker from "@/guiide/components/guaranteedIncome/guaranteedIncomeChecker.vue";
import { formatter } from "@/shared/ts/formatter";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import { app } from "@/guiide/ts/app";
import AnnuityType from "@/guiide/ts/annuityType";
import GuaranteedIncomeDesignBehaviour from "@/guiide/ts/guaranteedIncome/guaranteedIncomeDesignBehaviour";
import { useUIContent } from "@/guiide/ts/configuration/useUIContent";

export default defineComponent({
  name: "Scenario",
  components: {
    Modal,
    SolveButtons,
    GuaranteedIncomeChecker,
    Help
  },
  props: {
    scenario: { default: ScenarioType.AccordingToPlan, required: true },
    number: { default: "" },
    solveEnabled: { type: Boolean, required: false, default: false },
    guaranteedIncomeEnabled: { default: false, required: false },
    finishEncouragementEnabled: { default: false, required: false },
    showBorder: { default: true, required: false },
    showGraphButton: { default: false, required: false }
  },
  emits: ["solve-take-less", "solve-pay-more", "solve-equity-release", "show-graphs"],
  data() {
    return {
      userHasConsideredGuaranteedIncome: false,
      guaranteedIncomeConsideredAndThenSubscribed: null as null | boolean,
      disableButtons: false,
      HelpText
    };
  },
  setup() {
    const solveButtons = ref(null);
    const guaranteedIncomeModal = ref(null);
    const guaranteedIncomeChecker = ref(null);
    const { text } = useUIContent();

    return {
      solveButtons,
      guaranteedIncomeModal,
      guaranteedIncomeChecker,
      text
    };
  },
  watch: {
    "$store.state.retiree.guaranteedIncome.subscribed"(
      subscribed: null | boolean
    ) {
      if (
        subscribed != null &&
        !subscribed &&
        this.guaranteedIncomeConsideredAndThenSubscribed
      ) {
        this.resetGuaranteedIncomeState();
      }
    }
  },
  computed: {
    source: function(): Scenario {
      switch (this.scenario) {
        case ScenarioType.AccordingToPlan:
          return this.$store.state.accordingToPlanScenario;
        case ScenarioType.LiveThreeYearsLonger:
          return this.$store.state.youLiveLongerScenario;
        case ScenarioType.PotsFallTwentyPercent:
          return this.$store.state.marketCrashScenario;
        case ScenarioType.NeedExtraMoneyForLastTwoYears:
          return this.$store.state.needSomeCareScenario;
        case ScenarioType.LowerGrowth:
          return this.$store.state.lowerGrowthScenario;
        default:
          throw Error("Unknown scenario type.");
      }
    },
    getScenario(): Scenario {
      return this.source;
    },
    exclaimationOrCheck(): string {
      switch (this.source.outcome) {
        case ScenarioOutcome.Unknown:
          return "bi-question-circle-fill text-secondary";
        case ScenarioOutcome.Pass:
          return "bi-check-circle-fill text-success";
        default:
          return "bi-exclamation-circle-fill text-danger";
      }
    },
    showSolvePass: function(): boolean {
      return (
        this.solveEnabled &&
        !(this.showGuaranteedIncomeOffer || this.showGuaranteedIncomeUptake) &&
        !this.showFinishEncouragement &&
        this.source.outcome === ScenarioOutcome.Pass
      );
    },
    showSolveFail: function(): boolean {
      return (
        this.solveEnabled &&
        !(this.showGuaranteedIncomeOffer || this.showGuaranteedIncomeUptake) &&
        !this.showFinishEncouragement &&
        this.source.outcome === ScenarioOutcome.Fail
      );
    },
    failedScenarioText: function(): string {
      let scenarioKey: string = ScenarioInfo.getScenarioContentKey(this.scenario);
      const contentKey = `tests.scenarios.${scenarioKey}.warning`;
      const variantKey = this.hasPensionPots ? "with-pension-pots" : "without-pension-pots";
      return this.text(contentKey, variantKey);
    },
    isCurrentlySubscribedToGI: function(): boolean {
      return this.$store.state.retiree?.guaranteedIncome.subscribed ?? false;
    },
    showGuaranteedIncomeOffer: function(): boolean {
      if (this.$store.state.retiree == null) {
        return false;
      }

      if (!this.$store.state.configuration.guaranteedIncomeEnabled) {
        return false;
      }

      const designBehaviour: GuaranteedIncomeDesignBehaviour = this.$store.state.configuration.designGuaranteedIncomeBehaviour;
      if (designBehaviour === GuaranteedIncomeDesignBehaviour.Hidden) {
        return false;
      }

      if (this.$store.state.retiree.pensionPots.pots.length == 0) {
        return false;
      }

      if (
        this.userHasConsideredGuaranteedIncome &&
        !this.guaranteedIncomeConsideredAndThenSubscribed
      ) {
        return false;
      }

      return (
        !this.isCurrentlySubscribedToGI &&
        this.guaranteedIncomeEnabled &&
        this.source.outcome === ScenarioOutcome.Pass
      );
    },
    guaranteedIncomeOfferIsAdviceOnly: function(): boolean {
      const designBehaviour: GuaranteedIncomeDesignBehaviour = this.$store.state.configuration.designGuaranteedIncomeBehaviour;
      return designBehaviour === GuaranteedIncomeDesignBehaviour.AdvisoryOnly;
    },
    showGuaranteedIncomeUptake: function(): boolean {
      return (
        (this.userHasConsideredGuaranteedIncome &&
          this.guaranteedIncomeConsideredAndThenSubscribed) ||
        (this.isCurrentlySubscribedToGI && this.guaranteedIncomeEnabled)
      );
    },
    showFinishEncouragement: function(): boolean {
      return (
        this.source.outcome === ScenarioOutcome.Pass &&
        this.finishEncouragementEnabled &&
        !(this.showGuaranteedIncomeOffer || this.showGuaranteedIncomeUptake)
      );
    },
    annuityType: function(): AnnuityType {
      return this.$store.state.configuration.annuityType;
    },
    guaranteedIncomeUptakeText: function(): string {
      if (!this.isCurrentlySubscribedToGI) {
        return "A guaranteed income purchase has been included in your plan. The details are available on the Haves page.";
      } else {
        const annualIncome = formatter.formatCurrency(
          this.$store.state.retiree.guaranteedIncome.annualIncome
        );
        let text = `An illustrative guaranteed income, paying ${annualIncome} each year, has been included in your plan and your plan is still expected to work.`;
        if (this.annuityType == AnnuityType.GuaranteedIncome) {
          text = text + " This product may also have a surrender value option which is included in the pots chart.";
        }
        return text;
      }
    },
    textClass(): string {
      switch (this.source.outcome) {
        case ScenarioOutcome.Unknown:
          return "text-secondary";
        case ScenarioOutcome.Pass:
          return "text-success";
        default:
          return "text-danger";
      }
    },
    scenarioName(): string {
      return ScenarioInfo.getName(this.source.scenarioType);
    },
    hasPensionPots: function(): boolean {
      return this.$store.state.retiree?.pensionPots.pots.length > 0;
    }
  },
  methods: {
    getCardClass() {
      return this.showBorder ? "border-secondary shadow-sm" : "border-0";
    },
    onExamineGuaranteedIncome() {
      this.guaranteedIncomeModal.showModal();
      this.guaranteedIncomeChecker.checkIfCanBuyGuaranteedIncome();
    },
    onGuaranteedIncomeCheckFinished(subscribed: boolean) {
      this.userHasConsideredGuaranteedIncome = true;
      this.guaranteedIncomeConsideredAndThenSubscribed = subscribed;
      this.guaranteedIncomeModal.closeModal();
    },
    resetGuaranteedIncomeState() {
      this.userHasConsideredGuaranteedIncome = false;
      this.guaranteedIncomeConsideredAndThenSubscribed = false;
    },
    setDisabledState(disabled: boolean) {
      if (this.solveButtons != null) {
        this.solveButtons.setDisabledState(disabled);
      }
    },
    async onUnsubscribeGuaranteedIncomeClicked(event: any) {
      event.preventDefault();
      await app.deleteGuaranteedIncome();
    },
    onShowGraphsClicked() {
      this.$emit("show-graphs", this.scenario);
    },
    takeFocus(key: string): boolean {
      if (!this.showSolveFail) {
        return false;
      }

      return this.solveButtons.takeFocus("");
    }
  }
});
</script>

<style scoped>
    .card-scenario {
        border-radius: 1rem !important;
        padding: 30px;
        -webkit-box-shadow: 0px 0px 26px 8px rgba(0,0,0,0.2);
        -moz-box-shadow: 0px 0px 26px 8px rgba(0,0,0,0.2);
        box-shadow: 0px 0px 26px 8px rgba(0,0,0,0.04) !important;
        border: 0
    }

    @media (max-width: 576px) {
        .card-scenario  {
            padding: 30px 10px;
        }
    }

    #solveText {
        color: #d72714 !important;
    }

    .guaranteedIncomeText {
        color: #178830 !important;
    }

    #finishEncouragementText {
        color: #178830 !important;
    }

    #guaranteedIncomeUptakeText1 {
        color: #178830 !important;
    }

    #guaranteedIncomeUptakeText2 {
        color: #178830 !important;
    }

    .card-scenario .btn {
        border-radius: 50px;
    }

    .card-scenario h3 {
        font-size: 32px;
    }

    .text-success {
        color: #178830 !important;
    }
</style>
